import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { Loader } from "@jti/core-components/components/Loader";
import { newrelic } from "@jti/fe-clients";
import { getSessionStorage } from "@jti/rest-api";
import { useSelector } from "@jti/store/reduxHooks";
import { CheckoutV2LoyaltyChoice } from "@jti/store/slices/checkoutV2Slice";
import { useTranslations } from "@jti/ui";

import { LOYALTY_CART_POINTS_STORAGE_KEY } from "@checkout-v2/features/LoyaltyPoints/utils";
import { useCustomerOrder } from "@checkout-v2/shared/hooks/useCustomerOrder";
import { useCheckoutV2Configuration } from "@checkout-v2/shared/providers/CheckoutV2ConfigurationProvider";

import { LoyaltyPreviewDescription } from "../../components/LoyaltyPreviewDescription";
import { PaymentMethodsSection } from "../../components/PaymentMethodsSection";
import { PaymentMethodsSectionPreview } from "../../components/PaymentMethodsSectionPreview";
import type { LoyaltyPreviewInfo } from "../../components/PaymentMethodsSectionPreview/PaymentMethodsSectionPreview.types";
import { useAvailablePaymentMethodsForOrder } from "../../hooks/useAvailablePaymentMethodsForOrder";
import { usePreparedPaymentOptions } from "../../hooks/usePreparedPaymentOptions";

import styles from "./PaymentMethodsOrderContainer.module.scss";

type PaymentMethodsContainerProps = {
  mode: "edit" | "view";
  isPaymentFailedAlertVisible?: boolean;
};

const { sanitize } = dompurify;

const logError = (error: unknown) => {
  if (error instanceof Error) {
    newrelic.noticeError(error);
  } else {
    newrelic.noticeError(new Error("Unknown error in PaymentMethodsOrderContainer"));
  }
};

const getLoyaltyInfo = (
  loyaltyDiscountAmount: number | undefined,
  loyaltyDiscountPoints: number | undefined,
  loyaltyPointsToBeCollected: number
): LoyaltyPreviewInfo | undefined => {
  if (loyaltyDiscountAmount) {
    return {
      choice: CheckoutV2LoyaltyChoice.REDEEM,
      points: loyaltyDiscountPoints,
      voucherPrice: loyaltyDiscountAmount,
    };
  }

  if (loyaltyPointsToBeCollected > 0) {
    return {
      choice: CheckoutV2LoyaltyChoice.COLLECT,
      points: loyaltyPointsToBeCollected,
    };
  }

  return undefined;
};

export const PaymentMethodsOrderContainer = ({
  mode,
  isPaymentFailedAlertVisible = false,
}: PaymentMethodsContainerProps) => {
  const sessionStorage = getSessionStorage();
  const { __ } = useTranslations();
  const config = useConfig();
  const checkoutConfiguration = useCheckoutV2Configuration();

  const {
    magentoMethods,
    evoMethods,
    isLoading: isLoadingPaymentMethods,
    isError: isPaymentMethodsLoadingError,
    error,
  } = useAvailablePaymentMethodsForOrder();
  const orderQueryState = useCustomerOrder();
  const isOrderFreeOfCharge = Boolean(orderQueryState.data?.isZeroValueOrder);
  const selectedPaymentMethodCode = useSelector((state) => state.checkoutV2.data?.paymentMethodCode);
  const securedFieldsPayment = useSelector((state) => state.checkoutV2.data?.securedFieldsPayment);
  const loyaltyPointsToBeCollected = sessionStorage.get(LOYALTY_CART_POINTS_STORAGE_KEY, 0);
  const loyaltyDiscountAmount = orderQueryState.data?.prices?.appliedCampaign?.loyalty_discount_amount;
  const loyaltyDiscountPoints = orderQueryState.data?.prices?.appliedCampaign?.loyalty_discount_points;
  const loyaltyInfo = getLoyaltyInfo(loyaltyDiscountAmount, loyaltyDiscountPoints, loyaltyPointsToBeCollected);

  const paymentOptions = usePreparedPaymentOptions({
    evoMethods,
    magentoMethods,
    selectedShippingMethodCode: orderQueryState.data?.shippingMethod.methodCode,
    orderItems: orderQueryState.data?.items,
    methodAndCarrierCode: orderQueryState.data?.methodAndCarrierCode,
  });

  if (isLoadingPaymentMethods) {
    return <Loader />;
  }

  if (isOrderFreeOfCharge) {
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: sanitize(checkoutConfiguration.general.zeroValueOrderNotificationText),
        }}
      />
    );
  }

  if (isPaymentMethodsLoadingError || paymentOptions.length < 1) {
    logError(error);

    return (
      <AlertBanner
        tone="error"
        title={__("cart.order.payment_method.no_methods.title")}
        description={__("cart.order.payment_method.no_methods.description")}
        actions={[{ href: config.CONTACT_US_URL, label: __("cart.order.payment_method.no_methods.cta") }]}
      />
    );
  }

  return (
    <>
      {isPaymentFailedAlertVisible && (
        <div className={styles.alertBanner}>
          <AlertBanner
            tone="error"
            title={__("cart.checkout.payment_form.error.payment_failed_title")}
            description={__("cart.checkout.payment_form.error.payment_failed_description")}
          />
        </div>
      )}
      {mode === "view" ? (
        <PaymentMethodsSectionPreview
          paymentOptions={paymentOptions}
          selectedPaymentMethodCode={selectedPaymentMethodCode}
          isLoyaltyInfoVisible={!!loyaltyInfo}
          isRedeemVisible={false}
          loyaltyInfo={loyaltyInfo}
          securedFieldsPayment={securedFieldsPayment}
        />
      ) : (
        <>
          {loyaltyInfo && (
            <div>
              <p className={styles.loyaltyTitle}>{__("cart.checkout.loyalty.preview.title")}</p>
              <LoyaltyPreviewDescription loyaltyInfo={loyaltyInfo} isRedeemVisible={false} />
            </div>
          )}
          <PaymentMethodsSection
            paymentOptions={paymentOptions}
            selectedPaymentMethodCode={selectedPaymentMethodCode}
            submitButtonText={__("order.summary.payment_method.submit_button")}
          />
        </>
      )}
    </>
  );
};
